import { Button, Modal } from 'antd';

import { useSaveRoomBulkEdit } from 'app/hooks/data/core/extranet/room/useRoomBulkEdit';
import type {
  BulkEditPayload,
  OpeningDays,
} from 'app/typings/core/extranet/rooms/roomBulkEdit';
import { formatDateWithoutHour } from 'app/utils/dates';
import { pluralize } from 'app/utils/strings';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { InventoryTabItemName } from '../../../../InventoryStore';
import { useInventoryState } from '../../../../hooks';

import { PricingRuleBulkEditAlert } from './PricingRuleRoomBulkEditAlert';
import styles from './ValidationRoomBulkEditModal.module.scss';

type Props = {
  isOpened: boolean;
  handleClose: () => void;
  setIsValidationBulkEditModalOpen: (
    isValidationBulkEditModalOpen: boolean
  ) => void;
  hotelId: number;
  payload: BulkEditPayload;
  openingDays: OpeningDays;
  autoDiscountRate?: number;
};

export const ValidationRoomBulkEditModal = ({
  isOpened,
  handleClose,
  setIsValidationBulkEditModalOpen,
  hotelId,
  payload,
  openingDays,
  autoDiscountRate,
}: Props) => {
  const { timePeriod } = useInventoryState();

  const { mutateAsync: bulkEditSave } = useSaveRoomBulkEdit(hotelId, {
    startDate: formatDateWithoutHour(timePeriod.startDate),
    endDate: formatDateWithoutHour(timePeriod.endDate),
  });

  const handleCancel = () => {
    setIsValidationBulkEditModalOpen(false);
    amplitudeTrack('Return to Bulk Edit Changes', {
      errors: openingDays.unpublishedDayCount,
      tab: InventoryTabItemName.Rooms,
    });
  };

  const handleSaveChange = async () => {
    await bulkEditSave({
      ...payload,
    });

    amplitudeTrack('Accept Bulk Edit Changes', {
      stockPublished: openingDays.publishedDayCount,
      stockNotPublished: openingDays.unpublishedDayCount,
      nbAutoDiscountDeactivated: autoDiscountRate
        ? openingDays.publishedDayCount + openingDays.unpublishedDayCount
        : 0,
      tab: InventoryTabItemName.Rooms,
    });

    setIsValidationBulkEditModalOpen(false);
    handleClose();
  };

  return (
    <Modal
      className={styles.modal}
      open={isOpened}
      title="Apply your changes?"
      onCancel={handleCancel}
      wrapClassName={styles.validationBulkEditModal}
      footer={[
        <Button size="large" key="back" type="ghost" onClick={handleCancel}>
          Back to edit
        </Button>,
        <Button
          key="save"
          size="large"
          type="primary"
          htmlType="submit"
          onClick={handleSaveChange}
        >
          Save changes
        </Button>,
      ]}
    >
      <p>By saving your changes, this room will be:</p>
      <ul>
        <li>
          available on{' '}
          <b>{pluralize(openingDays.publishedDayCount, 'day', 'days')}</b>
        </li>
        <li>
          unavailable on{' '}
          {pluralize(openingDays.unpublishedDayCount, 'day', 'days')}
        </li>
      </ul>
      {autoDiscountRate && <PricingRuleBulkEditAlert />}
    </Modal>
  );
};
